import "./add-edit-panel.css";
import { AnalyzeAPI } from "@ai360/core";
import { messages } from "../i18n-messages";
import { IQueryExtractionRow } from "../data/sagas";
import { useIntl } from "@ai360/core/intl";
import SlidingPanel from "~/sliding-panel/sliding-panel";
import { ButtonTypes } from "~/core/components/button/button";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    DialogBox,
    Loader,
    LoaderTypes,
    Section,
    SelectInput,
    SubSection,
    TextArea,
    TextInput,
} from "~/core";

interface IQueryExtractionAddEditPanelProps {
    selectedRow: null | IQueryExtractionRow;
    closePanel: () => void;
}

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 28 }, (_, i) => ({
    label: (i + 1).toString(),
    value: i + 1,
}));

const FILE_EXTENTION_OPTIONS = [
    { label: "CSV", value: ".csv" },
    { label: "PARQUET", value: ".parquet" },
    { label: "XLSX", value: ".xlsx" },
];

const QueryExtractionAddEditPanel = ({
    closePanel,
    selectedRow,
}: IQueryExtractionAddEditPanelProps) => {
    const { formatMessage } = useIntl();

    const FREQUENCY_OPTIONS = [
        { label: formatMessage(messages.daily), value: "Daily" },
        { label: formatMessage(messages.weekly), value: "Weekly" },
        { label: formatMessage(messages.monthly), value: "Monthly" },
    ];

    const DAY_OF_WEEK_OPTIONS = [
        { label: formatMessage(messages.monday), value: 1 },
        { label: formatMessage(messages.tuesday), value: 2 },
        { label: formatMessage(messages.wednesday), value: 3 },
        { label: formatMessage(messages.thursday), value: 4 },
        { label: formatMessage(messages.friday), value: 5 },
        { label: formatMessage(messages.saturday), value: 6 },
        { label: formatMessage(messages.sunday), value: 7 },
    ];

    const queryId = selectedRow?.queryId;

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [fileName, setFileName] = useState("");
    const [fileExtention, setFileExtention] = useState<string>(FILE_EXTENTION_OPTIONS[0].value);
    const [outputUrl, setOutputUrl] = useState("");
    const [frequency, setFrequency] = useState("");
    const [dayOfWeek, setDayOfWeek] = useState<null | number>(null);
    const [dayOfMonth, setDayOfMonth] = useState<null | number>(null);
    const [queryText, setQueryText] = useState("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [validationMessage, setValidationMessage] = useState<null | string[]>(null);
    const [validationHeader, setValidationHeader] = useState<string>("");

    const isEdit = selectedRow !== null;

    useEffect(() => {
        if (queryId === undefined) {
            setIsLoading(false);
            return;
        }
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await AnalyzeAPI.getQueryById(queryId);
                const lowerName = response.fileName.toLowerCase();
                const matchedExt = FILE_EXTENTION_OPTIONS.find((opt) =>
                    lowerName.endsWith(opt.value)
                );
                if (matchedExt) {
                    const baseName = response.fileName.slice(0, -matchedExt.value.length);
                    setFileName(baseName);
                    setFileExtention(matchedExt.value);
                } else {
                    setFileName(response.fileName);
                }

                setName(response.name);
                setDescription(response.description);
                setOutputUrl(response.outputUrl);
                setFrequency(response.frequency);
                setDayOfWeek(dayOfWeek);
                setDayOfMonth(response.dayOfMonth);
                setQueryText(response.queryText);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (frequency !== "Weekly") {
            setDayOfWeek(null);
        }
        if (frequency !== "Monthly") {
            setDayOfMonth(null);
        }
    }, [frequency]);

    const onValidateQuery = async () => {
        if (!queryText.trim()) {
            setValidationMessage([formatMessage(messages.queryIsARequiredField)]);
            return;
        } else {
            setIsLoading(true);
        }

        try {
            const response = await AnalyzeAPI.reviewQuery({ queryText: queryText });
            if (response.error) {
                setValidationMessage(response.errorMessages);
                setValidationHeader(formatMessage(messages.invalidSqlHeader));
            } else {
                setValidationMessage([formatMessage(messages.validSqlText)]);
                setValidationHeader(formatMessage(messages.validSqlHeader));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const isFormValid = useMemo(() => {
        if (!name.trim()) return false;
        if (!fileName.trim()) return false;
        if (!outputUrl.trim()) return false;
        if (!frequency) return false;
        if (!queryText.trim()) return false;
        if (frequency === "Weekly" && dayOfWeek == null) return false;
        if (frequency === "Monthly" && dayOfMonth == null) return false;
        return true;
    }, [name, fileName, outputUrl, frequency, queryText, dayOfWeek, dayOfMonth]);

    const onSave = async () => {
        try {
            setIsLoading(true);
            const requestObj = {
                dayOfMonth: dayOfMonth,
                dayOfWeek: dayOfWeek,
                description: description,
                fileName: fileName + fileExtention,
                frequency: frequency,
                name: name,
                outputUrl: outputUrl,
                queryText: queryText,
            };
            await AnalyzeAPI.addUpdateQuery(requestObj, selectedRow?.queryId);
            closePanel();
        } catch (err) {
            if (err?.["apiResultObj"]?.["status"] === 400)
                setValidationHeader(formatMessage(messages.validationErrors));
            const details = err?.["apiResultObj"]?.["errors"].map((obj) => obj.message);
            setValidationMessage(details);
        } finally {
            setIsLoading(false);
        }
    };

    const reviewDialogBox = (
        <DialogBox
            className="delete-confirmation-modal"
            draggable={true}
            isOpen={validationMessage !== null}
            onClose={() => {
                setValidationMessage(null);
            }}
            testId="query-extraction-review-dialog-box"
            title={validationHeader}
        >
            <p>
                {(validationMessage ?? []).map((message, index) => (
                    <span key={index}>
                        {message}
                        {index < validationMessage.length - 1 && (
                            <>
                                <br />
                                <br />
                            </>
                        )}
                    </span>
                ))}
            </p>
        </DialogBox>
    );

    const addEditPanelRenderer = () => (
        <div className="add-edit-panel query-extraction-add-edit-panel">
            {validationMessage ? reviewDialogBox : null}
            {!isLoading ? null : <Loader type={LoaderTypes.LINE_SCALE} />}
            {isEdit ? (
                <SubSection testId="query-extraction-id-subsection">
                    <p className="query-id-display" data-testid="query-extraction-id">
                        {formatMessage(messages.queryIdLabel)}: {selectedRow.queryId}
                    </p>
                </SubSection>
            ) : null}
            <div className="section-container">
                <Section className="form-fields-section" testId="query-extraction-details-section">
                    <SubSection testId="query-extraction-name-subsection">
                        <TextInput
                            maxLength={255}
                            onChange={(value) => setName(value)}
                            placeholderText={formatMessage(messages.name)}
                            required
                            testId="query-extraction-name-input"
                            value={name}
                        />
                    </SubSection>
                    <SubSection testId="query-extraction-description-subsection">
                        <TextArea
                            maxLength={800000000}
                            onChange={(value) => setDescription(value)}
                            placeholderText={formatMessage(messages.description)}
                            testId="query-extraction-description-input"
                            value={description}
                        />
                    </SubSection>

                    <SubSection
                        className="frequency-row"
                        testId="query-extraction-filename-subsection"
                    >
                        <TextInput
                            maxLength={255}
                            onChange={(value) => setFileName(value)}
                            placeholderText={formatMessage(messages.fileName)}
                            required
                            testId="query-extraction-filename-input"
                            value={fileName}
                        />
                        <SelectInput
                            clearable={false}
                            onChange={(value) => setFileExtention(value)}
                            options={FILE_EXTENTION_OPTIONS}
                            placeholderText={formatMessage(messages.fileExtention)}
                            required
                            testId="query-extraction-file-extention-select"
                            value={fileExtention}
                        />
                    </SubSection>
                    <SubSection testId="query-extraction-output-url-subsection">
                        <TextInput
                            maxLength={800000000}
                            onChange={(value) => setOutputUrl(value)}
                            placeholderText={formatMessage(messages.outputUrl)}
                            required
                            testId="query-extraction-output-url-input"
                            value={outputUrl}
                        />
                    </SubSection>
                    <SubSection
                        className="frequency-row"
                        testId="query-extraction-frequency-subsection"
                    >
                        <SelectInput
                            clearable={false}
                            onChange={(value) => setFrequency(value)}
                            options={FREQUENCY_OPTIONS}
                            placeholderText={formatMessage(messages.frequency)}
                            required
                            testId="query-extraction-frequency-select"
                            value={frequency}
                        />
                        {frequency === "Monthly" ? (
                            <SelectInput
                                clearable={false}
                                onChange={(value) => setDayOfMonth(Number(value))}
                                options={DAY_OF_MONTH_OPTIONS}
                                placeholderText={formatMessage(messages.dayOfMonth)}
                                required
                                testId="query-extraction-day-of-month-select"
                                value={dayOfMonth}
                            />
                        ) : null}
                        {frequency === "Weekly" ? (
                            <SelectInput
                                clearable={false}
                                onChange={(value) => setDayOfWeek(Number(value))}
                                options={DAY_OF_WEEK_OPTIONS}
                                placeholderText={formatMessage(messages.dayOfWeek)}
                                required
                                testId="query-extraction-day-of-week-select"
                                value={dayOfWeek}
                            />
                        ) : null}
                    </SubSection>
                </Section>
                <Section className="query-section">
                    <div className="query-text-container">
                        <TextArea
                            height={500}
                            maxLength={800000000}
                            onChange={(value: string) => setQueryText(value)}
                            placeholderText={formatMessage(messages.query)}
                            required
                            testId="query-extraction-query-text-area"
                            value={queryText}
                        />
                        <div className="query-validate-button-container">
                            <Button
                                disabled={!queryText.trim()}
                                onClick={onValidateQuery}
                                testId="query-extraction-validate-button"
                                type={ButtonTypes.button}
                                value={formatMessage(messages.validate)}
                            />
                        </div>
                    </div>
                </Section>
            </div>
        </div>
    );

    const addEditPanelRendererRef = useRef(addEditPanelRenderer);
    addEditPanelRendererRef.current = addEditPanelRenderer;
    const SlidingPanelContent = useMemo(() => () => addEditPanelRendererRef.current(), []);

    return (
        <SlidingPanel
            component={SlidingPanelContent}
            navigateTo={{ childNameCode: "439", parentNameCode: "101" }}
        >
            <Button
                disabled={!isFormValid}
                forceSubmit
                onClick={onSave}
                testId="query-extraction-save-button"
                type={ButtonTypes.button}
                value="save"
            />
            <Button
                className="user-cancel"
                onClick={closePanel}
                testId="query-extraction-cancel-button"
                type={ButtonTypes.cancel}
            />
        </SlidingPanel>
    );
};

export default QueryExtractionAddEditPanel;
